/**
 * Forecasting drawing primitives — TradingView-style.
 *
 * Tools: Long Position, Short Position, Forecast, Date Range,
 *        Date & Price Range, Bars Pattern, Ghost Feed
 */

const HANDLE_RADIUS = 5;
const HANDLE_COLOR = '#6366f1';
const HANDLE_HIT = 8;
const HIT_TOLERANCE = 7;

function drawHandle(ctx, x, y, hpr, vpr) {
  ctx.beginPath();
  ctx.arc(x * hpr, y * vpr, HANDLE_RADIUS * hpr, 0, Math.PI * 2);
  ctx.fillStyle = HANDLE_COLOR; ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5 * hpr; ctx.stroke();
}

function hexToRgba(hex, a) {
  const c = hex.replace('#', '');
  return `rgba(${parseInt(c.slice(0,2),16)},${parseInt(c.slice(2,4),16)},${parseInt(c.slice(4,6),16)},${a})`;
}

class Base {
  constructor() { this._chart = null; this._series = null; this._requestUpdate = null; this.isHovered = false; this.isSelected = false; this.lineWidth = undefined; this.lineStyle = undefined; }
  attached({ requestUpdate, chart, series }) { this._requestUpdate = requestUpdate; this._chart = chart; this._series = series; }
  detached() { this._requestUpdate = null; this._chart = null; this._series = null; }
  requestUpdate() { this._requestUpdate?.(); }
  hitTest() { return Infinity; }
  isNear(x, y) { return this.hitTest(x, y) <= HIT_TOLERANCE; }
  hitTestHandle() { return -1; }
  moveBy() {}
  moveHandle() {}
  _ts() { return this._chart?.timeScale(); }
  _toXY(logical, price) {
    return { x: this._ts()?.logicalToCoordinate(logical), y: this._series?.priceToCoordinate(price) };
  }
}

// ═══════════════════════════════════════════════════════════════════
// POSITION (Long / Short) — Entry, SL, TP with R:R box
// ═══════════════════════════════════════════════════════════════════

class PositionPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      if (!s._entry || s._entry.x == null || !s._tp || !s._sl) return;

      const isLong = s.direction === 'long';
      const ex = s._entry.x * hpr, ey = s._entry.y * vpr;
      const tpy = s._tp.y * vpr, sly = s._sl.y * vpr;
      const width = 200 * hpr;
      const x1 = ex, x2 = ex + width;

      // TP zone (green)
      const tpColor = 'rgba(34,197,94,';
      ctx.save();
      ctx.fillStyle = tpColor + '0.12)';
      ctx.fillRect(x1, Math.min(ey, tpy), width, Math.abs(tpy - ey));
      ctx.strokeStyle = tpColor + '0.6)';
      ctx.lineWidth = 1.5 * hpr;
      ctx.setLineDash([4 * hpr, 3 * hpr]);
      ctx.beginPath(); ctx.moveTo(x1, tpy); ctx.lineTo(x2, tpy); ctx.stroke();

      // SL zone (red)
      const slColor = 'rgba(239,68,68,';
      ctx.fillStyle = slColor + '0.12)';
      ctx.fillRect(x1, Math.min(ey, sly), width, Math.abs(sly - ey));
      ctx.strokeStyle = slColor + '0.6)';
      ctx.beginPath(); ctx.moveTo(x1, sly); ctx.lineTo(x2, sly); ctx.stroke();

      // Entry line (solid)
      ctx.setLineDash([]);
      ctx.strokeStyle = '#a78bfa';
      ctx.lineWidth = 2 * hpr;
      ctx.beginPath(); ctx.moveTo(x1, ey); ctx.lineTo(x2, ey); ctx.stroke();

      // Hover glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba('#a78bfa', 0.25);
        ctx.lineWidth = 6 * hpr;
        ctx.strokeRect(x1, Math.min(tpy, sly), width, Math.abs(tpy - sly));
      }

      // Labels
      ctx.setLineDash([]);
      const fontSize = 10 * vpr;
      ctx.font = `bold ${fontSize}px -apple-system,sans-serif`;
      ctx.textBaseline = 'middle';

      // R:R ratio
      const risk = Math.abs(s.entryPrice - s.slPrice);
      const reward = Math.abs(s.tpPrice - s.entryPrice);
      const rr = risk > 0 ? (reward / risk).toFixed(2) : '∞';

      // TP label
      const tpPnl = Math.abs(s.tpPrice - s.entryPrice).toFixed(5);
      const tpPct = s.entryPrice !== 0 ? (Math.abs(s.tpPrice - s.entryPrice) / s.entryPrice * 100).toFixed(2) : '0';
      const tpLabel = `TP: ${s.tpPrice.toFixed(5)}  (+${tpPnl} / ${tpPct}%)`;
      ctx.fillStyle = tpColor + '0.85)';
      const tpLabelY = isLong ? tpy + fontSize : tpy - fontSize * 0.3;
      ctx.fillText(tpLabel, x1 + 6 * hpr, tpLabelY);

      // SL label
      const slPnl = Math.abs(s.entryPrice - s.slPrice).toFixed(5);
      const slPct = s.entryPrice !== 0 ? (Math.abs(s.entryPrice - s.slPrice) / s.entryPrice * 100).toFixed(2) : '0';
      const slLabel = `SL: ${s.slPrice.toFixed(5)}  (-${slPnl} / ${slPct}%)`;
      ctx.fillStyle = slColor + '0.85)';
      const slLabelY = isLong ? sly - fontSize * 0.3 : sly + fontSize;
      ctx.fillText(slLabel, x1 + 6 * hpr, slLabelY);

      // Entry + R:R label
      const entryLabel = `${isLong ? 'LONG' : 'SHORT'}  @${s.entryPrice.toFixed(5)}  R:R ${rr}`;
      ctx.fillStyle = '#a78bfa';
      ctx.fillText(entryLabel, x1 + 6 * hpr, ey - fontSize * 0.8);

      // Handles
      if (s.isSelected) {
        drawHandle(ctx, s._entry.x, s._entry.y, hpr, vpr);
        drawHandle(ctx, s._entry.x + 200/2, s._tp.y, hpr, vpr);
        drawHandle(ctx, s._entry.x + 200/2, s._sl.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class PositionPrimitive extends Base {
  constructor(entryPt, slPt, options = {}) {
    super();
    this.direction = options.direction || 'long';
    this.entryPt = entryPt;
    this.entryPrice = entryPt.price;
    this.slPrice = slPt.price;
    // Auto-compute TP for 2:1 R:R
    const risk = Math.abs(this.entryPrice - this.slPrice);
    if (this.direction === 'long') {
      this.tpPrice = this.entryPrice + risk * 2;
    } else {
      this.tpPrice = this.entryPrice - risk * 2;
    }
    this.color = options.color || '#a78bfa';
    this._entry = null; this._tp = null; this._sl = null;
    this._view = new PositionPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._entry = this._toXY(this.entryPt.logical, this.entryPrice);
    this._tp = this._toXY(this.entryPt.logical, this.tpPrice);
    this._sl = this._toXY(this.entryPt.logical, this.slPrice);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._entry || !this._tp || !this._sl) return Infinity;
    const ex = this._entry.x, w = 200;
    const top = Math.min(this._tp.y, this._sl.y);
    const bot = Math.max(this._tp.y, this._sl.y);
    return (x >= ex && x <= ex + w && y >= top && y <= bot) ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._entry && Math.hypot(x - this._entry.x, y - this._entry.y) < HANDLE_HIT) return 0;
    if (this._tp && Math.hypot(x - (this._entry.x + 100), y - this._tp.y) < HANDLE_HIT) return 1;
    if (this._sl && Math.hypot(x - (this._entry.x + 100), y - this._sl.y) < HANDLE_HIT) return 2;
    return -1;
  }
  moveBy(dL, dP) {
    this.entryPt = { logical: this.entryPt.logical + dL, price: this.entryPt.price + dP };
    this.entryPrice += dP; this.tpPrice += dP; this.slPrice += dP;
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) { // entry
      const dP = p - this.entryPrice;
      this.entryPt = { logical: l, price: p };
      this.entryPrice = p; this.tpPrice += dP; this.slPrice += dP;
    } else if (i === 1) { // TP
      this.tpPrice = p;
    } else if (i === 2) { // SL
      this.slPrice = p;
    }
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) {
    this.slPrice = pt.price;
    const risk = Math.abs(this.entryPrice - this.slPrice);
    if (this.direction === 'long') {
      this.tpPrice = this.entryPrice + risk * 2;
    } else {
      this.tpPrice = this.entryPrice - risk * 2;
    }
    this._requestUpdate?.();
  }
}

// ═══════════════════════════════════════════════════════════════════
// FORECAST — Multi-point projected price path
// ═══════════════════════════════════════════════════════════════════

class ForecastPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = s._screenPts;
      if (!pts || pts.length < 2) return;

      ctx.save();

      // Glow on hover
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.25);
        ctx.lineWidth = 6 * hpr;
        ctx.beginPath();
        ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
        ctx.stroke();
      }

      // Main path
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2 * hpr;
      ctx.setLineDash([6 * hpr, 4 * hpr]);
      ctx.beginPath();
      ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
      ctx.stroke();

      // Arrow at end
      if (pts.length >= 2) {
        const last = pts[pts.length - 1], prev = pts[pts.length - 2];
        const angle = Math.atan2((last.y - prev.y) * vpr, (last.x - prev.x) * hpr);
        const arrowLen = 10 * hpr;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(last.x * hpr, last.y * vpr);
        ctx.lineTo(last.x * hpr - arrowLen * Math.cos(angle - 0.4), last.y * vpr - arrowLen * Math.sin(angle - 0.4));
        ctx.moveTo(last.x * hpr, last.y * vpr);
        ctx.lineTo(last.x * hpr - arrowLen * Math.cos(angle + 0.4), last.y * vpr - arrowLen * Math.sin(angle + 0.4));
        ctx.stroke();
      }

      // Points
      ctx.setLineDash([]);
      for (const pt of pts) {
        ctx.beginPath();
        ctx.arc(pt.x * hpr, pt.y * vpr, 3 * hpr, 0, Math.PI * 2);
        ctx.fillStyle = s.color;
        ctx.fill();
      }

      // Label
      ctx.font = `bold ${9 * vpr}px -apple-system,sans-serif`;
      ctx.fillStyle = s.color;
      ctx.textBaseline = 'bottom';
      ctx.fillText('FORECAST', pts[0].x * hpr + 5 * hpr, pts[0].y * vpr - 5 * vpr);

      // Handles
      if (s.isSelected) {
        for (const pt of pts) drawHandle(ctx, pt.x, pt.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class ForecastPrimitive extends Base {
  constructor(points, options = {}) {
    super();
    this.points = [...points]; // array of {logical, price}
    this.color = options.color || '#818cf8';
    this._screenPts = [];
    this._view = new ForecastPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._screenPts = this.points.map(p => this._toXY(p.logical, p.price));
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const pts = this._screenPts;
    for (let i = 1; i < pts.length; i++) {
      const p1 = pts[i-1], p2 = pts[i];
      if (p1.x == null || p2.x == null) continue;
      const dx = p2.x - p1.x, dy = p2.y - p1.y, len2 = dx*dx + dy*dy;
      if (len2 === 0) continue;
      let t = Math.max(0, Math.min(1, ((x - p1.x)*dx + (y - p1.y)*dy) / len2));
      const d = Math.hypot(x - (p1.x + t*dx), y - (p1.y + t*dy));
      if (d <= HIT_TOLERANCE) return d;
    }
    return Infinity;
  }
  hitTestHandle(x, y) {
    for (let i = 0; i < this._screenPts.length; i++) {
      const p = this._screenPts[i];
      if (p && Math.hypot(x - p.x, y - p.y) < HANDLE_HIT) return i;
    }
    return -1;
  }
  moveBy(dL, dP) {
    this.points = this.points.map(p => ({ logical: p.logical + dL, price: p.price + dP }));
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i >= 0 && i < this.points.length) {
      this.points[i] = { logical: l, price: p };
      this._requestUpdate?.();
    }
  }
  addPoint(pt) {
    this.points.push(pt);
    this._requestUpdate?.();
  }
  setLastPoint(pt) {
    if (this.points.length > 0) {
      this.points[this.points.length - 1] = pt;
      this._requestUpdate?.();
    }
  }
  setPreviewPoint(pt) { this.setLastPoint(pt); }
}

// ═══════════════════════════════════════════════════════════════════
// DATE RANGE — Horizontal time measurement (2 points)
// ═══════════════════════════════════════════════════════════════════

class DateRangePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;

      const x1 = p1.x * hpr, x2 = p2.x * hpr;
      const midY = bs.height / 2;

      ctx.save();

      // Hover glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 8 * hpr;
        ctx.beginPath(); ctx.moveTo(x1, midY); ctx.lineTo(x2, midY); ctx.stroke();
      }

      // Vertical extent lines
      ctx.strokeStyle = hexToRgba(s.color, 0.3);
      ctx.lineWidth = 1 * hpr;
      ctx.setLineDash([3 * hpr, 3 * hpr]);
      ctx.beginPath();
      ctx.moveTo(x1, 0); ctx.lineTo(x1, bs.height);
      ctx.moveTo(x2, 0); ctx.lineTo(x2, bs.height);
      ctx.stroke();

      // Shaded zone
      ctx.fillStyle = hexToRgba(s.color, 0.06);
      ctx.fillRect(Math.min(x1, x2), 0, Math.abs(x2 - x1), bs.height);

      // Horizontal arrow line
      ctx.setLineDash([]);
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5 * hpr;
      ctx.beginPath(); ctx.moveTo(x1, midY); ctx.lineTo(x2, midY); ctx.stroke();

      // Arrows
      const dir = x2 > x1 ? 1 : -1;
      const al = 8 * hpr;
      ctx.beginPath();
      ctx.moveTo(x2, midY);
      ctx.lineTo(x2 - dir * al, midY - al * 0.5);
      ctx.moveTo(x2, midY);
      ctx.lineTo(x2 - dir * al, midY + al * 0.5);
      ctx.stroke();

      // Label
      const bars = Math.abs(s.pt2.logical - s.pt1.logical);
      const label = `${bars} bars`;
      ctx.font = `bold ${11 * vpr}px -apple-system,sans-serif`;
      const tw = ctx.measureText(label).width;
      const lx = (x1 + x2) / 2 - tw / 2;
      const ly = midY - 14 * vpr;

      ctx.fillStyle = hexToRgba(s.color, 0.9);
      ctx.beginPath(); ctx.roundRect(lx - 6 * hpr, ly - 2 * vpr, tw + 12 * hpr, 18 * vpr, 4 * hpr); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
      ctx.fillText(label, lx, ly + 7 * vpr);

      if (s.isSelected) { drawHandle(ctx, p1.x, bs.height / vpr / 2, hpr, vpr); drawHandle(ctx, p2.x, bs.height / vpr / 2, hpr, vpr); }
      ctx.restore();
    })}};
  }
}

export class DateRangePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null;
    this._view = new DateRangePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const minX = Math.min(this._p1.x, this._p2.x), maxX = Math.max(this._p1.x, this._p2.x);
    return (x >= minX - 5 && x <= maxX + 5) ? Math.abs(y - (this._p1.y + this._p2.y) / 2) : Infinity;
  }
  hitTestHandle(x, y) {
    const midY = this._p1 ? (this._p1.y + this._p2.y) / 2 : 0;
    if (this._p1 && Math.hypot(x - this._p1.x, y - midY) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - midY) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// DATE & PRICE RANGE — Combined measurement box (2 points)
// ═══════════════════════════════════════════════════════════════════

class DatePriceRangePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;

      const x1 = p1.x * hpr, y1 = p1.y * vpr, x2 = p2.x * hpr, y2 = p2.y * vpr;

      ctx.save();

      // Filled box
      const isUp = s.priceDiff >= 0;
      const boxColor = isUp ? '34,197,94' : '239,68,68';
      ctx.fillStyle = `rgba(${boxColor},0.08)`;
      ctx.fillRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));

      // Border
      ctx.strokeStyle = `rgba(${boxColor},0.5)`;
      ctx.lineWidth = 1.5 * hpr;
      ctx.setLineDash([4 * hpr, 3 * hpr]);
      ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));

      // Diagonal
      ctx.strokeStyle = `rgba(${boxColor},0.7)`;
      ctx.lineWidth = 1 * hpr;
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

      // Label
      const bars = Math.abs(s.pt2.logical - s.pt1.logical);
      const priceDiff = s.priceDiff;
      const pct = s.pt1.price !== 0 ? ((priceDiff / s.pt1.price) * 100).toFixed(2) : '0';
      const sign = priceDiff >= 0 ? '+' : '';
      const pips = Math.abs(priceDiff * 100000).toFixed(1);
      const label = `${bars} bars  |  ${sign}${priceDiff.toFixed(5)} (${sign}${pct}%)  |  ${pips} pips`;

      ctx.font = `bold ${11 * vpr}px -apple-system,sans-serif`;
      ctx.setLineDash([]);
      const tw = ctx.measureText(label).width;
      const lx = (x1 + x2) / 2 - tw / 2;
      const ly = Math.min(y1, y2) - 22 * vpr;

      ctx.fillStyle = `rgba(${boxColor},0.9)`;
      ctx.beginPath(); ctx.roundRect(lx - 6 * hpr, ly - 2 * vpr, tw + 12 * hpr, 18 * vpr, 4 * hpr); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
      ctx.fillText(label, lx, ly + 7 * vpr);

      if (s.isSelected) { drawHandle(ctx, p1.x, p1.y, hpr, vpr); drawHandle(ctx, p2.x, p2.y, hpr, vpr); }
      ctx.restore();
    })}};
  }
}

export class DatePriceRangePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this.priceDiff = 0;
    this._p1 = null; this._p2 = null;
    this._view = new DatePriceRangePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
    this.priceDiff = this.pt2.price - this.pt1.price;
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const r = { x: Math.min(this._p1.x, this._p2.x), y: Math.min(this._p1.y, this._p2.y),
      w: Math.abs(this._p2.x - this._p1.x), h: Math.abs(this._p2.y - this._p1.y) };
    return (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// BARS PATTERN — Copy a section of bars and project it forward
// ═══════════════════════════════════════════════════════════════════

class BarsPatternPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = s._screenPts;
      if (!pts || pts.length < 2) return;

      ctx.save();

      // Hover glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 8 * hpr;
        ctx.beginPath();
        ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
        ctx.stroke();
      }

      // Filled area
      ctx.fillStyle = hexToRgba(s.color, 0.06);
      ctx.beginPath();
      ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
      ctx.lineTo(pts[pts.length-1].x * hpr, bs.height);
      ctx.lineTo(pts[0].x * hpr, bs.height);
      ctx.closePath();
      ctx.fill();

      // Path line
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2 * hpr;
      ctx.setLineDash([5 * hpr, 3 * hpr]);
      ctx.beginPath();
      ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
      ctx.stroke();

      // Label
      ctx.setLineDash([]);
      ctx.font = `bold ${9 * vpr}px -apple-system,sans-serif`;
      ctx.fillStyle = s.color;
      ctx.textBaseline = 'bottom';
      ctx.fillText('PATTERN', pts[0].x * hpr + 5 * hpr, pts[0].y * vpr - 5 * vpr);

      // Handles
      if (s.isSelected) {
        for (const pt of pts) drawHandle(ctx, pt.x, pt.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class BarsPatternPrimitive extends Base {
  constructor(points, options = {}) {
    super();
    this.points = [...points];
    this.color = options.color || '#06b6d4';
    this._screenPts = [];
    this._view = new BarsPatternPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._screenPts = this.points.map(p => this._toXY(p.logical, p.price));
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const pts = this._screenPts;
    for (let i = 1; i < pts.length; i++) {
      const p1 = pts[i-1], p2 = pts[i];
      if (p1.x == null || p2.x == null) continue;
      const dx = p2.x - p1.x, dy = p2.y - p1.y, len2 = dx*dx + dy*dy;
      if (len2 === 0) continue;
      let t = Math.max(0, Math.min(1, ((x - p1.x)*dx + (y - p1.y)*dy) / len2));
      const d = Math.hypot(x - (p1.x + t*dx), y - (p1.y + t*dy));
      if (d <= HIT_TOLERANCE) return d;
    }
    return Infinity;
  }
  hitTestHandle(x, y) {
    for (let i = 0; i < this._screenPts.length; i++) {
      const p = this._screenPts[i];
      if (p && Math.hypot(x - p.x, y - p.y) < HANDLE_HIT) return i;
    }
    return -1;
  }
  moveBy(dL, dP) {
    this.points = this.points.map(p => ({ logical: p.logical + dL, price: p.price + dP }));
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i >= 0 && i < this.points.length) {
      this.points[i] = { logical: l, price: p };
      this._requestUpdate?.();
    }
  }
  addPoint(pt) { this.points.push(pt); this._requestUpdate?.(); }
  setLastPoint(pt) {
    if (this.points.length > 0) { this.points[this.points.length - 1] = pt; this._requestUpdate?.(); }
  }
  setPreviewPoint(pt) { this.setLastPoint(pt); }
}

// ═══════════════════════════════════════════════════════════════════
// GHOST FEED — Translucent projected future path (like forecast but
// with filled candle-style rendering)
// ═══════════════════════════════════════════════════════════════════

class GhostFeedPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = s._screenPts;
      if (!pts || pts.length < 2) return;

      ctx.save();
      ctx.globalAlpha = 0.4;

      // Glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.3);
        ctx.lineWidth = 8 * hpr;
        ctx.beginPath();
        ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
        ctx.stroke();
      }

      // Ghost candle bodies (small rectangles between consecutive points)
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i-1], cur = pts[i];
        if (prev.x == null || cur.x == null) continue;
        const px = prev.x * hpr, py = prev.y * vpr;
        const cx = cur.x * hpr, cy = cur.y * vpr;
        const isUp = cy <= py;
        const bodyW = Math.max(4 * hpr, Math.abs(cx - px) * 0.6);
        const bodyX = cx - bodyW / 2;

        // Wick
        ctx.strokeStyle = isUp ? 'rgba(34,197,94,0.6)' : 'rgba(239,68,68,0.6)';
        ctx.lineWidth = 1 * hpr;
        const wickTop = Math.min(py, cy) - 5 * vpr;
        const wickBot = Math.max(py, cy) + 5 * vpr;
        ctx.beginPath();
        ctx.moveTo(cx, wickTop); ctx.lineTo(cx, wickBot);
        ctx.stroke();

        // Body
        ctx.fillStyle = isUp ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)';
        ctx.strokeStyle = isUp ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)';
        ctx.lineWidth = 1 * hpr;
        const bodyTop = Math.min(py, cy), bodyH = Math.max(2 * vpr, Math.abs(cy - py));
        ctx.fillRect(bodyX, bodyTop, bodyW, bodyH);
        ctx.strokeRect(bodyX, bodyTop, bodyW, bodyH);
      }

      // Label
      ctx.globalAlpha = 0.7;
      ctx.font = `bold ${9 * vpr}px -apple-system,sans-serif`;
      ctx.fillStyle = s.color;
      ctx.textBaseline = 'bottom';
      ctx.fillText('GHOST', pts[0].x * hpr + 5 * hpr, pts[0].y * vpr - 5 * vpr);

      // Handles
      ctx.globalAlpha = 1;
      if (s.isSelected) {
        for (const pt of pts) drawHandle(ctx, pt.x, pt.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class GhostFeedPrimitive extends Base {
  constructor(points, options = {}) {
    super();
    this.points = [...points];
    this.color = options.color || '#94a3b8';
    this._screenPts = [];
    this._view = new GhostFeedPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._screenPts = this.points.map(p => this._toXY(p.logical, p.price));
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const pts = this._screenPts;
    for (let i = 1; i < pts.length; i++) {
      const p1 = pts[i-1], p2 = pts[i];
      if (p1.x == null || p2.x == null) continue;
      const dx = p2.x - p1.x, dy = p2.y - p1.y, len2 = dx*dx + dy*dy;
      if (len2 === 0) continue;
      let t = Math.max(0, Math.min(1, ((x - p1.x)*dx + (y - p1.y)*dy) / len2));
      const d = Math.hypot(x - (p1.x + t*dx), y - (p1.y + t*dy));
      if (d <= HIT_TOLERANCE) return d;
    }
    return Infinity;
  }
  hitTestHandle(x, y) {
    for (let i = 0; i < this._screenPts.length; i++) {
      const p = this._screenPts[i];
      if (p && Math.hypot(x - p.x, y - p.y) < HANDLE_HIT) return i;
    }
    return -1;
  }
  moveBy(dL, dP) {
    this.points = this.points.map(p => ({ logical: p.logical + dL, price: p.price + dP }));
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i >= 0 && i < this.points.length) {
      this.points[i] = { logical: l, price: p };
      this._requestUpdate?.();
    }
  }
  addPoint(pt) { this.points.push(pt); this._requestUpdate?.(); }
  setLastPoint(pt) {
    if (this.points.length > 0) { this.points[this.points.length - 1] = pt; this._requestUpdate?.(); }
  }
  setPreviewPoint(pt) { this.setLastPoint(pt); }
}

// ═══════════════════════════════════════════════════════════════════
// TEXT — Resizable text box on chart (1 click, double-click to edit)
// ═══════════════════════════════════════════════════════════════════

class TextLabelPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p = s._pos, p2 = s._pos2;
      if (!p || p.x == null) return;
      const px = p.x * hpr, py = p.y * vpr;
      const fontSize = (s.fontSize || 12) * vpr;
      ctx.save();
      ctx.font = `${s.bold ? 'bold ' : ''}${fontSize}px -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
      ctx.textBaseline = 'top';

      // Box dimensions — use explicit size if set, otherwise auto from text
      const pad = 6 * hpr;
      let boxW, boxH;
      if (p2 && p2.x != null) {
        boxW = Math.abs(p2.x - p.x) * hpr; boxH = Math.abs(p2.y - p.y) * vpr;
      } else {
        const lines = s.text.split('\n');
        const lineH = fontSize * 1.35;
        const widths = lines.map(l => ctx.measureText(l).width);
        boxW = Math.max(...widths, 40 * hpr) + pad * 2;
        boxH = lines.length * lineH + pad * 2;
      }
      if (boxW < 30 * hpr) boxW = 30 * hpr;
      if (boxH < 20 * vpr) boxH = 20 * vpr;

      // Background
      if (s.showBg) {
        ctx.fillStyle = hexToRgba(s.color, 0.12);
        ctx.beginPath(); ctx.roundRect(px, py, boxW, boxH, 4 * hpr); ctx.fill();
      }
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, s.isSelected ? 0.5 : 0.3);
        ctx.lineWidth = (s.isSelected ? 1.5 : 1) * hpr;
        ctx.setLineDash(s.showBg ? [] : [3 * hpr, 3 * hpr]);
        ctx.beginPath(); ctx.roundRect(px, py, boxW, boxH, 4 * hpr); ctx.stroke();
        ctx.setLineDash([]);
      }

      // Clip text to box
      ctx.beginPath(); ctx.rect(px, py, boxW, boxH); ctx.clip();
      ctx.fillStyle = s.color;
      const lines = s.text.split('\n');
      const lineH = fontSize * 1.35;
      for (let i = 0; i < lines.length; i++) ctx.fillText(lines[i], px + pad, py + pad + i * lineH);

      ctx.restore();

      // Store screen bounds for editor overlay
      s._screenBounds = { x: px / hpr, y: py / vpr, w: boxW / hpr, h: boxH / vpr };
      s._bounds = { x: p.x, y: p.y, w: boxW / hpr, h: boxH / vpr };

      // Handles: 0=top-left (move), 1=bottom-right (resize)
      if (s.isSelected) {
        drawHandle(ctx, p.x, p.y, hpr, vpr);
        drawHandle(ctx, p.x + boxW / hpr, p.y + boxH / vpr, hpr, vpr);
      }
    })}};
  }
}

export class TextLabelPrimitive extends Base {
  constructor(pt, options = {}) {
    super(); this.pt = pt; this.pt2 = options.pt2 || null; // bottom-right for sizing
    this.text = options.text || 'Text'; this.color = options.color || '#cdd6f4';
    this.fontSize = options.fontSize || 12; this.bold = options.bold || false;
    this.showBg = options.showBg !== undefined ? options.showBg : true;
    this.isEditable = true;
    this._pos = null; this._pos2 = null; this._bounds = null; this._screenBounds = null;
    this._view = new TextLabelPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._pos = this._toXY(this.pt.logical, this.pt.price);
    if (this.pt2) this._pos2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) { const b = this._bounds; if (!b) return Infinity; return (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) ? 0 : Infinity; }
  hitTestHandle(x, y) {
    if (!this._bounds) return -1;
    const b = this._bounds;
    if (this._pos && Math.hypot(x - this._pos.x, y - this._pos.y) < HANDLE_HIT) return 0;
    const brx = b.x + b.w, bry = b.y + b.h;
    if (Math.hypot(x - brx, y - bry) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt = { logical: this.pt.logical + dL, price: this.pt.price + dP };
    if (this.pt2) this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) {
      const dL = l - this.pt.logical, dP = p - this.pt.price;
      this.pt = { logical: l, price: p };
      if (this.pt2) this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    } else {
      this.pt2 = { logical: l, price: p };
    }
    this._requestUpdate?.();
  }
  setText(text) { this.text = text; this._requestUpdate?.(); }
  getScreenBounds() { return this._screenBounds; }
}

// ═══════════════════════════════════════════════════════════════════
// ANCHORED TEXT — Text with anchor dot + connecting line (resizable)
// ═══════════════════════════════════════════════════════════════════

class AnchoredTextPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p = s._pos, p2 = s._pos2;
      if (!p || p.x == null) return;
      const px = p.x * hpr, py = p.y * vpr;
      ctx.save();
      const fontSize = (s.fontSize || 11) * vpr;
      ctx.font = `bold ${fontSize}px -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
      const pad = 8 * hpr, offsetX = 20 * hpr;

      // Box dimensions
      let boxW, boxH;
      if (p2 && p2.x != null) {
        boxW = Math.abs(p2.x - p.x - offsetX / hpr) * hpr; boxH = Math.abs(p2.y - p.y) * vpr * 2;
      } else {
        const lines = s.text.split('\n');
        const lineH = fontSize * 1.3;
        const widths = lines.map(l => ctx.measureText(l).width);
        boxW = Math.max(...widths, 40 * hpr) + pad * 2;
        boxH = lines.length * lineH + pad * 2;
      }
      if (boxW < 40 * hpr) boxW = 40 * hpr;
      if (boxH < 24 * vpr) boxH = 24 * vpr;

      const boxX = px + offsetX, boxY = py - boxH / 2;

      // Anchor dot
      ctx.beginPath(); ctx.arc(px, py, 4 * hpr, 0, Math.PI * 2); ctx.fillStyle = s.color; ctx.fill();
      // Line
      ctx.strokeStyle = s.color; ctx.lineWidth = 1 * hpr;
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(boxX, py); ctx.stroke();
      // Box
      ctx.fillStyle = hexToRgba(s.color, 0.1); ctx.strokeStyle = hexToRgba(s.color, 0.5); ctx.lineWidth = 1 * hpr;
      ctx.beginPath(); ctx.roundRect(boxX, boxY, boxW, boxH, 5 * hpr); ctx.fill(); ctx.stroke();
      if (s.isHovered || s.isSelected) { ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 3 * hpr; ctx.stroke(); }

      // Clip + text
      ctx.beginPath(); ctx.rect(boxX, boxY, boxW, boxH); ctx.clip();
      ctx.fillStyle = s.color; ctx.textBaseline = 'top';
      const lines = s.text.split('\n');
      const lineH = fontSize * 1.3;
      for (let i = 0; i < lines.length; i++) ctx.fillText(lines[i], boxX + pad, boxY + pad + i * lineH);

      ctx.restore();

      s._screenBounds = { x: boxX / hpr, y: boxY / vpr, w: boxW / hpr, h: boxH / vpr };
      s._bounds = { x: p.x, y: p.y - boxH / 2 / vpr, w: (offsetX + boxW) / hpr, h: boxH / vpr };
      const brx = (boxX + boxW) / hpr, bry = (boxY + boxH) / vpr;
      s._resizeHandle = { x: brx, y: bry };

      if (s.isSelected) {
        drawHandle(ctx, p.x, p.y, hpr, vpr);
        drawHandle(ctx, brx, bry, hpr, vpr);
      }
    })}};
  }
}

export class AnchoredTextPrimitive extends Base {
  constructor(pt, options = {}) {
    super(); this.pt = pt; this.pt2 = options.pt2 || null;
    this.text = options.text || 'Note'; this.color = options.color || '#a78bfa';
    this.fontSize = options.fontSize || 11;
    this.isEditable = true;
    this._pos = null; this._pos2 = null; this._bounds = null; this._screenBounds = null; this._resizeHandle = null;
    this._view = new AnchoredTextPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._pos = this._toXY(this.pt.logical, this.pt.price);
    if (this.pt2) this._pos2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) { const b = this._bounds; if (!b) return Infinity; return (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) ? 0 : Infinity; }
  hitTestHandle(x, y) {
    if (this._pos && Math.hypot(x - this._pos.x, y - this._pos.y) < HANDLE_HIT) return 0;
    const rh = this._resizeHandle;
    if (rh && Math.hypot(x - rh.x, y - rh.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt = { logical: this.pt.logical + dL, price: this.pt.price + dP };
    if (this.pt2) this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) { this.pt = { logical: l, price: p }; }
    else { this.pt2 = { logical: l, price: p }; }
    this._requestUpdate?.();
  }
  setText(text) { this.text = text; this._requestUpdate?.(); }
  getScreenBounds() { return this._screenBounds; }
}

// ═══════════════════════════════════════════════════════════════════
// NOTE — Sticky note with colored top strip (resizable)
// ═══════════════════════════════════════════════════════════════════

class NotePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p = s._pos, p2 = s._pos2;
      if (!p || p.x == null) return;
      const px = p.x * hpr, py = p.y * vpr;
      ctx.save();
      const fontSize = (s.fontSize || 11) * vpr;
      ctx.font = `${s.bold ? 'bold ' : ''}${fontSize}px -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
      const pad = 10 * hpr;

      // Dimensions
      let noteW, noteH;
      if (p2 && p2.x != null) {
        noteW = Math.abs(p2.x - p.x) * hpr; noteH = Math.abs(p2.y - p.y) * vpr;
      } else {
        const lines = s.text.split('\n');
        const lineH = fontSize * 1.35;
        const widths = lines.map(l => ctx.measureText(l).width);
        noteW = Math.max(100 * hpr, ...widths) + pad * 2;
        noteH = Math.max(60 * vpr, lines.length * lineH) + pad * 2;
      }
      if (noteW < 60 * hpr) noteW = 60 * hpr;
      if (noteH < 40 * vpr) noteH = 40 * vpr;

      // Shadow
      ctx.shadowColor = 'rgba(0,0,0,0.3)'; ctx.shadowBlur = 8 * hpr; ctx.shadowOffsetY = 2 * vpr;
      ctx.fillStyle = hexToRgba(s.color, 0.15);
      ctx.beginPath(); ctx.roundRect(px, py, noteW, noteH, 4 * hpr); ctx.fill();
      ctx.shadowColor = 'transparent';
      // Top strip
      ctx.fillStyle = hexToRgba(s.color, 0.6);
      ctx.beginPath(); ctx.roundRect(px, py, noteW, 4 * vpr, [4 * hpr, 4 * hpr, 0, 0]); ctx.fill();
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.5); ctx.lineWidth = 1.5 * hpr;
        ctx.beginPath(); ctx.roundRect(px, py, noteW, noteH, 4 * hpr); ctx.stroke();
      }
      // Clip + text
      ctx.beginPath(); ctx.rect(px + pad, py + 8 * vpr, noteW - pad * 2, noteH - 12 * vpr); ctx.clip();
      ctx.fillStyle = '#cdd6f4'; ctx.textBaseline = 'top';
      const lines = s.text.split('\n');
      const lineH = fontSize * 1.35;
      for (let i = 0; i < lines.length; i++) ctx.fillText(lines[i], px + pad, py + 8 * vpr + i * lineH);
      ctx.restore();

      s._screenBounds = { x: px / hpr, y: py / vpr, w: noteW / hpr, h: noteH / vpr };
      s._bounds = { x: p.x, y: p.y, w: noteW / hpr, h: noteH / vpr };

      if (s.isSelected) {
        drawHandle(ctx, p.x, p.y, hpr, vpr);
        drawHandle(ctx, p.x + noteW / hpr, p.y + noteH / vpr, hpr, vpr);
      }
    })}};
  }
}

export class NotePrimitive extends Base {
  constructor(pt, options = {}) {
    super(); this.pt = pt; this.pt2 = options.pt2 || null;
    this.text = options.text || 'Note'; this.color = options.color || '#f59e0b';
    this.fontSize = options.fontSize || 11; this.bold = options.bold || false;
    this.isEditable = true;
    this._pos = null; this._pos2 = null; this._bounds = null; this._screenBounds = null;
    this._view = new NotePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._pos = this._toXY(this.pt.logical, this.pt.price);
    if (this.pt2) this._pos2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) { const b = this._bounds; if (!b) return Infinity; return (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) ? 0 : Infinity; }
  hitTestHandle(x, y) {
    if (!this._bounds) return -1;
    const b = this._bounds;
    if (this._pos && Math.hypot(x - this._pos.x, y - this._pos.y) < HANDLE_HIT) return 0;
    const brx = b.x + b.w, bry = b.y + b.h;
    if (Math.hypot(x - brx, y - bry) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt = { logical: this.pt.logical + dL, price: this.pt.price + dP };
    if (this.pt2) this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) {
      const dL = l - this.pt.logical, dP = p - this.pt.price;
      this.pt = { logical: l, price: p };
      if (this.pt2) this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    } else {
      this.pt2 = { logical: l, price: p };
    }
    this._requestUpdate?.();
  }
  setText(text) { this.text = text; this._requestUpdate?.(); }
  getScreenBounds() { return this._screenBounds; }
}

// ═══════════════════════════════════════════════════════════════════
// CALLOUT — Bubble with pointer to anchor (2 clicks, dbl-click edit)
// ═══════════════════════════════════════════════════════════════════

class CalloutPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const anchor = s._anchor, bubble = s._bubble;
      if (!anchor || !bubble || anchor.x == null || bubble.x == null) return;
      const ax = anchor.x * hpr, ay = anchor.y * vpr;
      const bx = bubble.x * hpr, by = bubble.y * vpr;
      ctx.save();
      const fontSize = (s.fontSize || 11) * vpr;
      ctx.font = `${s.bold ? 'bold ' : ''}${fontSize}px -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
      const lines = s.text.split('\n');
      const lineH = fontSize * 1.35;
      const widths = lines.map(l => ctx.measureText(l).width);
      const pad = 8 * hpr;
      // Use explicit size or auto
      let boxW, boxH;
      if (s.boxSize) {
        boxW = s.boxSize.w * hpr; boxH = s.boxSize.h * vpr;
      } else {
        const maxW = Math.max(60 * hpr, ...widths);
        boxW = maxW + pad * 2; boxH = lines.length * lineH + pad * 2;
      }
      if (boxW < 40 * hpr) boxW = 40 * hpr;
      if (boxH < 24 * vpr) boxH = 24 * vpr;
      // Pointer line
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5 * hpr;
      ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
      // Anchor dot
      ctx.beginPath(); ctx.arc(ax, ay, 3.5 * hpr, 0, Math.PI * 2); ctx.fillStyle = s.color; ctx.fill();
      // Bubble
      const bubbleX = bx - pad, bubbleY = by - boxH / 2;
      ctx.fillStyle = hexToRgba(s.color, 0.12); ctx.strokeStyle = hexToRgba(s.color, 0.5); ctx.lineWidth = 1 * hpr;
      ctx.beginPath(); ctx.roundRect(bubbleX, bubbleY, boxW, boxH, 6 * hpr); ctx.fill(); ctx.stroke();
      if (s.isHovered || s.isSelected) { ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 3 * hpr; ctx.stroke(); }
      // Clip + text
      ctx.beginPath(); ctx.rect(bubbleX, bubbleY, boxW, boxH); ctx.clip();
      ctx.fillStyle = '#cdd6f4'; ctx.textBaseline = 'top';
      for (let i = 0; i < lines.length; i++) ctx.fillText(lines[i], bx, bubbleY + pad + i * lineH);
      ctx.restore();

      s._screenBounds = { x: bubbleX / hpr, y: bubbleY / vpr, w: boxW / hpr, h: boxH / vpr };
      s._bounds = { ax: anchor.x, ay: anchor.y, bx: bubbleX / hpr, by: bubbleY / vpr, bw: boxW / hpr, bh: boxH / vpr };
      const brx = (bubbleX + boxW) / hpr, bry = (bubbleY + boxH) / vpr;
      s._resizeHandle = { x: brx, y: bry };

      if (s.isSelected) {
        drawHandle(ctx, anchor.x, anchor.y, hpr, vpr);
        drawHandle(ctx, bubble.x, bubble.y, hpr, vpr);
        drawHandle(ctx, brx, bry, hpr, vpr);
      }
    })}};
  }
}

export class CalloutPrimitive extends Base {
  constructor(anchorPt, bubblePt, options = {}) {
    super();
    this.anchorPt = anchorPt;
    this.bubblePt = bubblePt || { logical: anchorPt.logical + 5, price: anchorPt.price };
    this.text = options.text || 'Callout'; this.color = options.color || '#38bdf8';
    this.fontSize = options.fontSize || 11; this.bold = options.bold || false;
    this.boxSize = null; // { w, h } in screen px
    this.isEditable = true;
    this._anchor = null; this._bubble = null; this._bounds = null; this._screenBounds = null; this._resizeHandle = null;
    this._view = new CalloutPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._anchor = this._toXY(this.anchorPt.logical, this.anchorPt.price);
    this._bubble = this._toXY(this.bubblePt.logical, this.bubblePt.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const b = this._bounds; if (!b) return Infinity;
    if (Math.hypot(x - b.ax, y - b.ay) < HIT_TOLERANCE) return 0;
    if (x >= b.bx && x <= b.bx + b.bw && y >= b.by && y <= b.by + b.bh) return 0;
    return Infinity;
  }
  hitTestHandle(x, y) {
    if (this._anchor && Math.hypot(x - this._anchor.x, y - this._anchor.y) < HANDLE_HIT) return 0;
    if (this._bubble && Math.hypot(x - this._bubble.x, y - this._bubble.y) < HANDLE_HIT) return 1;
    const rh = this._resizeHandle;
    if (rh && Math.hypot(x - rh.x, y - rh.y) < HANDLE_HIT) return 2;
    return -1;
  }
  moveBy(dL, dP) {
    this.anchorPt = { logical: this.anchorPt.logical + dL, price: this.anchorPt.price + dP };
    this.bubblePt = { logical: this.bubblePt.logical + dL, price: this.bubblePt.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) this.anchorPt = { logical: l, price: p };
    else if (i === 1) this.bubblePt = { logical: l, price: p };
    else if (i === 2 && this._screenBounds) {
      // Resize: compute new box width/height from drag position
      const sb = this._screenBounds;
      this.boxSize = { w: Math.max(40, l - sb.x + (sb.w - (this._resizeHandle?.x - sb.x || 0))), h: Math.max(24, p - sb.y + (sb.h - (this._resizeHandle?.y - sb.y || 0))) };
      // Actually simpler: use pixel diff approach
      const rh = this._resizeHandle;
      if (rh) {
        const currentW = this._screenBounds.w, currentH = this._screenBounds.h;
        const ts = this._ts();
        const newL = ts?.coordinateToLogical(l) ?? l;
        // For callout resize, just track in screen px directly via bounds
        this.boxSize = { w: Math.max(40, currentW + (l - rh.x)), h: Math.max(24, currentH + (p - rh.y)) };
      }
    }
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.bubblePt = pt; this._requestUpdate?.(); }
  setText(text) { this.text = text; this._requestUpdate?.(); }
  getScreenBounds() { return this._screenBounds; }
}

// ═══════════════════════════════════════════════════════════════════
// PRICE LABEL — Label anchored to price scale (dbl-click to edit)
// ═══════════════════════════════════════════════════════════════════

class PriceLabelPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p = s._pos;
      if (!p || p.x == null) return;
      const px = p.x * hpr, py = p.y * vpr;
      ctx.save();
      const fontSize = 11 * vpr;
      ctx.font = `bold ${fontSize}px -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
      ctx.textBaseline = 'middle';
      const label = s.text || s.pt.price.toFixed(5);
      const tw = ctx.measureText(label).width;
      const pad = 6 * hpr, boxW = tw + pad * 2, boxH = 20 * vpr;
      // Dashed line to right
      ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 1 * hpr;
      ctx.setLineDash([3 * hpr, 3 * hpr]);
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(bs.width, py); ctx.stroke();
      // Label box
      ctx.setLineDash([]);
      const lx = bs.width - boxW - 4 * hpr;
      ctx.fillStyle = s.color;
      ctx.beginPath(); ctx.roundRect(lx, py - boxH / 2, boxW, boxH, 3 * hpr); ctx.fill();
      // Arrow
      ctx.beginPath(); ctx.moveTo(lx, py); ctx.lineTo(lx - 6 * hpr, py - 5 * vpr); ctx.lineTo(lx - 6 * hpr, py + 5 * vpr); ctx.closePath(); ctx.fill();
      // Text
      ctx.fillStyle = '#1e1e2e'; ctx.fillText(label, lx + pad, py);
      if (s.isHovered || s.isSelected) { ctx.strokeStyle = hexToRgba(s.color, 0.4); ctx.lineWidth = 2 * hpr; ctx.beginPath(); ctx.roundRect(lx, py - boxH / 2, boxW, boxH, 3 * hpr); ctx.stroke(); }
      s._screenBounds = { x: lx / hpr, y: (py - boxH / 2) / vpr, w: boxW / hpr, h: boxH / vpr };
      s._bounds = { x: p.x, y: p.y - boxH / 2 / vpr, w: bs.width / hpr, h: boxH / vpr };
      if (s.isSelected) drawHandle(ctx, p.x, p.y, hpr, vpr);
      ctx.restore();
    })}};
  }
}

export class PriceLabelPrimitive extends Base {
  constructor(pt, options = {}) {
    super(); this.pt = pt;
    this.text = options.text || ''; this.color = options.color || '#6366f1';
    this.isEditable = true;
    this._pos = null; this._bounds = null; this._screenBounds = null;
    this._view = new PriceLabelPV(this);
  }
  updateAllViews() { if (!this._chart || !this._series) return; this._pos = this._toXY(this.pt.logical, this.pt.price); }
  paneViews() { return [this._view]; }
  hitTest(x, y) { const b = this._bounds; if (!b) return Infinity; return (x >= b.x && y >= b.y && y <= b.y + b.h) ? 0 : Infinity; }
  hitTestHandle(x, y) { if (this._pos && Math.hypot(x - this._pos.x, y - this._pos.y) < HANDLE_HIT) return 0; return -1; }
  moveBy(dL, dP) { this.pt = { logical: this.pt.logical + dL, price: this.pt.price + dP }; this._requestUpdate?.(); }
  moveHandle(i, l, p) { this.pt = { logical: l, price: p }; this._requestUpdate?.(); }
  setText(text) { this.text = text; this._requestUpdate?.(); }
  getScreenBounds() { return this._screenBounds; }
}
